import React, { useEffect, Fragment, useContext, useState } from "react"

import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import ClickAwayListener from "@mui/material/ClickAwayListener"
import Paper from "@mui/material/Paper"
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip"
import { styled } from "@mui/material/styles"
import { useDispatch, useSelector } from "react-redux"
import { Link, useHistory, useLocation } from "react-router-dom"

import { setIsCreateTeeupOpen } from "@actions/createTeeupActions"
import { selectUserInfo } from "@selectors/user"
import { getRecentTeeups, RECENT_TEEUPS_EVENT } from "@utils/recentTeeups"
import { isDevBackend } from "@utils/devBackendShim"
import { isUserView, VIEW_MODE_EVENT } from "@utils/viewMode"

import {
  IconPlus,
  IconPlanflow,
  IconInbox,
  IconCalendar,
  IconMessages,
  IconPeople,
  IconSettings,
  IconChevronDown,
} from "./AleIcons"
import { filtersList } from "@utils/inbox"
import { useInboxStore } from "stores/inboxStore"

import "./alleRail.scss"
// SECURITY 2026-07-09: the legacy CreateTeeupAiModal (browser-side OpenAI, useOpenAi hook) is
// removed -- it embedded the OpenAI API key in the FE bundle AND was the retired toy-AI create
// path. The sidebar "AI create" entry now routes to /create-ai (the real backend-wired flow).
import CreateTeeupConfirmPopup from "@components/CreateTeeupConfirmPopup"
import {
  BOTTOM_SIDEBAR_MENU,
  NEW_SIDEBAR_MENU,
  SIDEBAR_MENU_NAMES,
} from "@constants/sidebar"
import { useScreenWidth } from "@hooks"
import { SvgIcon } from "@components/common"
import NotificationBell from "@components/NotificationBell"
import closeIcon from "@assets/images/newDesign/ndInviteesCross.svg"
import { selectIsCreateTeeupOpen } from "@selectors/createTeeUp"
import { selectUserId } from "@selectors/user"
import { showAiFeature } from "@utils/showAiFeature"
import { SideBarContext } from "@utils/sideBarContext"
import { CreateTeeupModal } from "components"
import { MEDIA_SIZES } from "assets/styles/mediaSizes"
import { useCreateTeeupStore } from "stores/createTeeupStore"

import { ToggleButton } from "./atoms"
import UserInfoPopover from "./UserInfoPopover"
import { NavItem } from "./molecules"
import {
  Wrapper,
  Logo,
  CrossWrapper,
  Navigation,
  FirstPartList,
  ListItem,
  Name,
  LogoWrapper,
  BottomPart,
  Title,
} from "./sidebarStyles"

const accordionStyles = {
  ".MuiAccordion-root::before": {
    content: "none",
  },
  ".MuiAccordionSummary-root": {
    borderBottomWidth: 0,
  },
  ".MuiAccordionSummary-root.Mui-expanded": {
    minHeight: "52px",
  },
}

const summuryStyles = {
  ".MuiAccordionSummary-content": {
    margin: "0px !important",
  },
  ".MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
}

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "transparent",
    maxWidth: 235,
    minWidth: 235,
    borderRadius: 8,
    fontSize: theme.typography.pxToRem(12),
  },
}))

export const NewSidebar = () => {
  const { pathname, search } = useLocation()

  const route = pathname?.slice(1)
  const menu = NEW_SIDEBAR_MENU[route] || NEW_SIDEBAR_MENU.common
  const bottomMenu = BOTTOM_SIDEBAR_MENU[route] || BOTTOM_SIDEBAR_MENU.common
  const defaultRoute = menu?.[0]?.items?.[0]

  const [activeRoute, setActiveRoute] = useState(defaultRoute)
  const [isProfileMenuOpen, toggleProfileMenuOpen] = useState(false)

  const [activeExpandedMenus, setActiveExpandedMenus] = useState([])
  const userId = useSelector(selectUserId)
  const userInfo = useSelector(selectUserInfo)
  // wave4-nav-ia: "Recent" section -- last ~5 viewed TeeUps (client-side, localStorage).
  const [recentTeeups, setRecentTeeups] = useState(() => getRecentTeeups(userId))
  useEffect(() => {
    const refresh = () => setRecentTeeups(getRecentTeeups(userId))
    refresh()
    window.addEventListener(RECENT_TEEUPS_EVENT, refresh)
    return () => window.removeEventListener(RECENT_TEEUPS_EVENT, refresh)
  }, [userId])
  // ticket #217: dev/user view toggle -- USER (default) hides the "Recent" QA-fixture list;
  // DEV shows it (still inside the existing More disclosure, unchanged placement/behavior).
  const [userView, setUserView] = useState(() => isUserView())
  useEffect(() => {
    const refresh = () => setUserView(isUserView())
    window.addEventListener(VIEW_MODE_EVENT, refresh)
    window.addEventListener("storage", refresh)
    return () => {
      window.removeEventListener(VIEW_MODE_EVENT, refresh)
      window.removeEventListener("storage", refresh)
    }
  }, [])
  // ticket #216 (web-nav-align-2026-07-09): secondary destinations + the "Recent" QA-fixture
  // list are collapsed under "More" by default -- nothing removed, just decluttered off the
  // default view (see nav-group.more below).
  const [showMore, setShowMore] = useState(false)
  const isCreateOpen = useSelector(selectIsCreateTeeupOpen)
  const dispatch = useDispatch()
  const history = useHistory()
  const { isSideBarOpen, focusCounter, handleToggleSidebar, setFocusCounter } =
    useContext(SideBarContext)
  const logoName = isSideBarOpen ? "CooWeBlueLogo" : "coowe-small-icon-blue"
  const { isSmallSize } = useScreenWidth()
  const isCreateConfirmOpen = useCreateTeeupStore(
    state => state.isConfirmCreateTeeupPopupOpen
  )

  useEffect(() => {
    if (pathname) {
      const items = menu.flatMap(item => item.items)
      const index = items.findIndex(item => item.pathname === pathname)

      setActiveExpandedMenus(
        [...activeExpandedMenus, items?.[index]?.group].filter(Boolean)
      )
    }
  }, [pathname])

  useEffect(() => {
    const option = new URLSearchParams(search).get("option")

    if (option) {
      // COMING-UP-cycle hardening (2026-07-03, pre-existing since the 2024-07-05 menu redesign):
      // an unknown ?option= (e.g. /settings?option=notifications -- the real menu key is
      // "notificationSettings") made findIndex return -1, so setActiveRoute(items[-1]) stored
      // undefined and renderMenuItems crashed the whole settings page reading activeRoute.option.
      // Alias the intuitive names, and on a genuinely unknown option KEEP the current/default
      // pane instead of crashing.
      const OPTION_ALIASES = {
        notifications: "notificationSettings",
        account: "accountManagement",
        invitations: "invitationsDetails",
      }
      const target = OPTION_ALIASES[option] || option
      const items = menu.flatMap(item => item.items)
      const found = items.find(item => item.option === target)

      if (found) setActiveRoute(found)
    }
  }, [search])

  const setIsCreateOpen = value => {
    dispatch(setIsCreateTeeupOpen(value))
  }


  const handleSetFocus = item => {
    if (document.body.offsetWidth <= Number.parseInt(MEDIA_SIZES.MOBILE)) {
      handleToggleSidebar(false)
    }

    const { name, itemIndex } = item
    switch (name) {
      case SIDEBAR_MENU_NAMES.AI_CREATE:
        // SECURITY/REAL-FLOW 2026-07-09: route to the real backend-wired AI create page instead of
        // the removed browser-side-OpenAI toy modal.
        history.push("/create-ai")
        break
      case SIDEBAR_MENU_NAMES.CREATE:
        // CREATE-AI-V1 2026-07-03: primary "+ Create" entry repoints to the new
        // prompt-first flow on dev backend only; classic modal is unchanged
        // everywhere else and one click away via "Use classic form" there.
        if (isDevBackend()) {
          history.push("/create-ai")
          break
        }
        history.push("/inbox")
        setIsCreateOpen(true)
        break
      default:
        setActiveRoute(item)
        setFocusCounter(itemIndex)
        break
    }
  }

  const renderMenuItems = menuItems => {
    return menuItems.map(item => {
      const isActive = item.option
        ? activeRoute?.option === item.option
        : focusCounter === item.index || activeRoute?.name === item.name
      const isShouldShow = showAiFeature(userId)

      if (item.name === SIDEBAR_MENU_NAMES.AI_CREATE && !isShouldShow) {
        return null
      }

      return (
        <ListItem
          $isActive={isActive}
          $isClickable
          testId={item.testId}
          $isWrapped={!isSideBarOpen}
          $isCreate={
            item.name === SIDEBAR_MENU_NAMES.CREATE ||
            item.name === SIDEBAR_MENU_NAMES.AI_CREATE
          }
          onClick={() => handleSetFocus(item)}
          key={`${item.name}-${item.index}`}>
          <NavItem isActive={isActive} item={item} />
        </ListItem>
      )
    })
  }

  const toggleMenuItems = groupName => {
    if (activeExpandedMenus.includes(groupName)) {
      setActiveExpandedMenus(
        activeExpandedMenus.filter(item => item !== groupName)
      )
    } else {
      setActiveExpandedMenus([...activeExpandedMenus, groupName])
    }
  }

  const goBack = () => {
    history.push("/inbox")
  }

  // wave4-nav-ia fix2: helpers for the ratified fixed nav structure.
  const isCommonMenu = menu === NEW_SIDEBAR_MENU.common
  const navRowClass = to =>
    "qj-nav__row" + (pathname === to ? " qj-nav__row--active" : "")
  const goToInboxTab = tabTitle => {
    const filter = filtersList.find(f => f.title === tabTitle)
    if (filter) useInboxStore.getState().setCurrentOpenTab(filter)
    history.push("/inbox")
  }

  return (
    <>
      {isCreateOpen && (
        <CreateTeeupModal onClose={() => setIsCreateOpen(false)} />
      )}

      {isCreateConfirmOpen && <CreateTeeupConfirmPopup />}

      <Wrapper $isWrapped={!isSideBarOpen} $isOpen={isSideBarOpen}>
        {!isSmallSize && <ToggleButton />}
        <LogoWrapper style={{ display: "flex", alignItems: "center" }}>
          <Link to="/inbox" data-testid="ale-brand">
            {/* wave4-fix8: the ORIGINAL CooWe logo mark + wordmark restored, recolored pink ->
                Ale's blue (#7FB1E3) via exact-fill-swap SVG variants (CooWeBlueLogo /
                coowe-small-icon-blue). Color swap ONLY -- geometry untouched per QJ. */}
            <Logo $isWrapped={!isSideBarOpen} name={logoName} />
          </Link>
          {/* wave4-nav-ia fix2: bell + small "Notifications" label live ON the logo row itself
              (right side), per QJ's ratified layout -- not a separate sidebar row. */}
          <NotificationBell variant="logo" isSideBarOpen={isSideBarOpen} />
          <CrossWrapper
            $isOpen={isSideBarOpen}
            onClick={() => handleToggleSidebar(false)}>
            <img src={closeIcon} alt="close" />
          </CrossWrapper>
        </LogoWrapper>

        <Navigation>
          <FirstPartList>
            {menu?.[0]?.title && (
              <Title onClick={goBack}>
                <svg
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <g id="Media / Icon/Unfilled/cheveron-down">
                    <path
                      id="Icon"
                      d="M12 15.1521L7 10.1521L12 5.15213"
                      stroke="#404040"
                      stroke-width="1.2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </g>
                </svg>
                <p>{menu[0].title}</p>
              </Title>
            )}
            {isCommonMenu ? (
              /* wave4-nav-ia fix4: VERBATIM transplant of Alle's prototype rail (coowe.html
                 .nav/.nav-group/.nav-label/.nav-item/.lbl/.recent + his CSS variables, scoped
                 under .alle-rail in alleRail.scss). Class structure and every visual property
                 come from his file; only React/router/icon mechanics adapted. */
              <nav className="alle-rail" data-testid="qj-nav">
                <div className="nav">
                  {/* QJ nav (2026-07-10, web-nav-3tab): the primary rail = EXACTLY three
                      destinations -- Planner · Inbox · People -- with a prominent round "+"
                      create button ABOVE them. Per QJ's target screenshot: TeeUp, Settings, and
                      the "More" disclosure are REMOVED from the rail. Settings lives under the
                      bottom user avatar (UserInfoPopover: Profile / Settings / Log out); the
                      notification bell sits on the logo row (top-right, with unread badge);
                      Coming-Up/Calendar lives inside the Planner page. Routes for the removed
                      rail entries (Calendar, Messages, Goals/Tasks/Booking/Prioritize,
                      Relationships) are unchanged and still reachable by URL / from within their
                      pages -- only the rail surface is trimmed to QJ's three tabs. */}
                  <div className="nav-create-wrap">
                    <button
                      type="button"
                      className="nav-create-round"
                      data-testid="create-teeup"
                      aria-label="Create"
                      onClick={() => {
                        // CREATE-AI-V1 2026-07-03: dev-backend-only repoint to the real
                        // backend-wired AI create page; classic modal everywhere else.
                        if (isDevBackend()) {
                          history.push("/create-ai")
                          return
                        }
                        history.push("/inbox")
                        setIsCreateOpen(true)
                      }}>
                      <IconPlus />
                    </button>
                  </div>

                  <div className="nav-group">
                    <button
                      type="button"
                      className={"nav-item" + (pathname === "/planflow" ? " active" : "")}
                      data-testid="nav-planflow"
                      onClick={() => history.push("/planflow")}>
                      <IconPlanflow />
                      <span className="lbl">Planner</span>
                    </button>
                    <button
                      type="button"
                      className={"nav-item" + (pathname === "/inbox" ? " active" : "")}
                      data-testid="nav-inbox"
                      onClick={() => goToInboxTab("Active")}>
                      <IconInbox />
                      <span className="lbl">Inbox</span>
                    </button>
                    <button
                      type="button"
                      className={"nav-item" + (pathname === "/people" ? " active" : "")}
                      data-testid="nav-people"
                      onClick={() => history.push("/people")}>
                      <IconPeople />
                      <span className="lbl">People</span>
                    </button>
                  </div>
                </div>
              </nav>
            ) : (
              menu.map((group, groupIndex) => (
                <Fragment key={`${group.name}-${groupIndex}`}>
                  {isSideBarOpen && group.name ? (
                    <Accordion
                      expanded={activeExpandedMenus.includes(group.name)}
                      onChange={() => toggleMenuItems(group.name)}
                      className="!m-0 !shadow-none [&:before]:!opacity-0"
                      sx={accordionStyles}>
                      <AccordionSummary
                        expandIcon={<SvgIcon name="arrow" />}
                        sx={summuryStyles}>
                        <ListItem $isGroup={group.name}>
                          <Name $isGroup>{group.name}</Name>
                        </ListItem>
                      </AccordionSummary>
                      <AccordionDetails className="!p-0">
                        {renderMenuItems(group.items)}
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    renderMenuItems(group.items)
                  )}
                </Fragment>
              ))
            )}
          </FirstPartList>

          <BottomPart>
            {bottomMenu.map(item => {
              const isActive = focusCounter === item.index

              return (
                <ListItem
                  $isActive={isActive}
                  $isClickable
                  $isWrapped={!isSideBarOpen}
                  $isProfileLink={item.name === SIDEBAR_MENU_NAMES.PROFILE}
                  onClick={() => handleSetFocus(item)}
                  key={`${item.name}-${item.index}`}>
                  <NavItem isActive={isActive} item={item} />
                </ListItem>
              )
            })}

            <ClickAwayListener onClickAway={() => toggleProfileMenuOpen(false)}>
              <div>
                <HtmlTooltip
                  onClose={() => toggleProfileMenuOpen(false)}
                  open={isProfileMenuOpen}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  title={
                    <Paper elevation={3}>
                      <UserInfoPopover
                        onPopoverClose={() => toggleProfileMenuOpen(false)}
                      />
                    </Paper>
                  }>
                  <ListItem
                    $isActive={false}
                    $isClickable
                    $isWrapped={!isSideBarOpen}
                    $isProfileLink
                    onClick={event => {
                      event.stopPropagation()
                      toggleProfileMenuOpen(!isProfileMenuOpen)
                    }}>
                    <NavItem
                      isActive={false}
                      disabled
                      profile
                      item={{
                        index: 13,
                        // wave4-nav-ia: bottom entry = avatar + the user's NAME (menu: Profile /
                        // Settings / Log out via the existing UserInfoPopover).
                        name: userInfo?.name || SIDEBAR_MENU_NAMES.PROFILE,
                        pathname: "/my-profile",
                      }}
                    />
                  </ListItem>
                </HtmlTooltip>
              </div>
            </ClickAwayListener>
          </BottomPart>
        </Navigation>
      </Wrapper>
    </>
  )
}
